import { find, isEmpty, toNumber } from 'lodash';
import { useRouter } from 'next/router';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import useInfluenzaHospitalFilters, {
  InfluenzaHospitalFilters,
} from '@/hooks/hospital/list/useInfluenzaHospitalFilters';
import { getAuthToken } from '@/recoil/atoms/common/auth';
import { SearchHospitalService } from '@/services/hospital/SearchHospitalService';
import {
  HospitalInfluenzaSearchRequestInput,
  HospitalSortType,
} from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import {
  InfluenzaHospitalVo,
  RegionByDepthVo,
  RegionName,
  SearchKoreaAddressV2Vo,
  SelectedRegion,
  UserAddressVo,
  UserCoordinate,
} from '@/types/hospital';
import { faceTofaceLogger, getPageName } from '@/utils/logger';
import {
  NATIVE_POST_MESSAGE_TYPE,
  isWebView,
  sendPostMessageToNative,
} from '@/utils/mobile/RNPlugins';

import useScreenFocus from '../../useScreenFocus';
// eslint-disable-next-line import/order
import useToast from '../../useToast';
import useGetAddressToCoordinateByIp from '../search/useGetAddressToCoordinateByIp';
import useGetUserAddressByLocation from '../search/useGetUserAddressByLocation';
import useGetUserAddresses from '../search/useGetUserAddresses';
import useSearchInfluenzaHospitalsInfinityQuery from '../search/useSearchInfluenzaHospitalsInfinityQuery';
import useSearchKoreaAddressV2 from '../search/useSearchKoreaAddressV2';

interface influenzaHospitalListViewModel extends InfluenzaHospitalFilters {
  fetchNextPage: () => void;
  isLoadingHospitals: boolean;
  hasNextPage: boolean;
  goToAppHospital: (hospitalPid: string) => void;
  goToAppMyPageAddressScreen: () => void;
  selectSavedAddress: (coordiname: UserCoordinate) => () => void;
  hospitals: InfluenzaHospitalVo[];
  updateCoordinate: (coord: UserCoordinate) => void;
  requestGeolocationPermission: () => void;
  addresses: UserAddressVo[];
  switchRegionFilter: () => void;
  isRegionFilterOpened: boolean;
  siDoList: SearchKoreaAddressV2Vo[];
  siGunGuList: SearchKoreaAddressV2Vo[];
  dongList: SearchKoreaAddressV2Vo[];
  selectedRegion: SelectedRegion;
  hasNoData: boolean;
  changeSiDo: (siDo: SearchKoreaAddressV2Vo, isAll?: boolean) => void;
  regionByDepth?: RegionByDepthVo;
  getHeaderAddress: () => string;
  getCurrentLocation: () => void;
  clickSiDo: (
    item: SearchKoreaAddressV2Vo,
    isAll?: boolean,
  ) => (event: MouseEvent<HTMLDivElement>) => void;
  clickSiGunGu: (
    item: SearchKoreaAddressV2Vo,
    isAll?: boolean,
  ) => (event: MouseEvent<HTMLDivElement>) => void;
  clickDong: (
    item: SearchKoreaAddressV2Vo,
  ) => (event: MouseEvent<HTMLDivElement>) => void;
  isSejong: boolean;
}

const useInfluenzaHospitalListViewModel =
  (): influenzaHospitalListViewModel => {
    const router = useRouter();
    const pageName = getPageName(router);
    const { showToast } = useToast();
    const isFocused = useScreenFocus();
    const queries = router.query as RegionByDepthVo;

    const isLoggedIn = !!getAuthToken();
    const { data: userCoordinateByIp } = useGetAddressToCoordinateByIp();

    const initialCoord = useMemo(() => {
      const initialCoordinate =
        SearchHospitalService.initializeUserCoordinate();

      if (!userCoordinateByIp) {
        return { ...initialCoordinate };
      }

      const latitude = toNumber(initialCoordinate.latitude);
      const longitude = toNumber(initialCoordinate.longitude);

      if (isNaN(latitude) || isNaN(longitude)) {
        return { ...initialCoordinate };
      }

      return { latitude, longitude };
    }, [userCoordinateByIp]);

    const [coordinate, setCoordinate] = useState<UserCoordinate>(initialCoord);

    const [isRegionFilterOpened, setIsRegionFilterOpened] =
      useState<boolean>(false);

    const [selectedRegion, setSelectedRegion] = useState<SelectedRegion>({
      siDo: null,
      siGunGu: null,
      dong: null,
    });
    const [regionByDepth, setRegionByDepth] =
      useState<RegionByDepthVo>(queries);

    const isSejong = selectedRegion.siDo?.regionCode === '36';

    const { data: siDoList = [] } = useSearchKoreaAddressV2({
      siDoCode: null,
      siGunGuCode: null,
    });
    const { data: siGunGuList = [] } = useSearchKoreaAddressV2({
      siDoCode: isSejong ? '' : selectedRegion.siDo?.regionCode || '',
      siGunGuCode: null,
    });
    const { data: dongList = [] } = useSearchKoreaAddressV2({
      siDoCode: selectedRegion.siDo?.regionCode || '',
      siGunGuCode: isSejong ? null : selectedRegion.siGunGu?.regionCode || '',
    });

    const {
      data: sigunguDongli = SearchHospitalService.initializeUserSigunguDongli(),
      refetch: addressFetch,
    } = useGetUserAddressByLocation(coordinate.latitude, coordinate.longitude);

    const influenzaHospitalFilterHook = useInfluenzaHospitalFilters();
    const { selectedInfluenzaFilters } = influenzaHospitalFilterHook;

    const searchHospitalsParams: Pick<
      HospitalInfluenzaSearchRequestInput,
      | 'geoPoint'
      | 'limit'
      | 'sortType'
      | 'regionByDepth'
      | 'freeVaccinationFilters'
    > = useMemo(
      () => ({
        geoPoint: coordinate,
        limit: 30,
        sortType: HospitalSortType.Distance,
        freeVaccinationFilters: selectedInfluenzaFilters.FreeInfluenza.dtoType
          ? [selectedInfluenzaFilters.FreeInfluenza.dtoType]
          : undefined,
      }),
      [coordinate, selectedInfluenzaFilters.FreeInfluenza],
    );

    const getSearchHospitalsParams =
      useCallback((): HospitalInfluenzaSearchRequestInput => {
        if (!regionByDepth?.siDoName) {
          return searchHospitalsParams;
        }

        return {
          ...searchHospitalsParams,
          regionByDepth: {
            legalEupMyeonDongName: regionByDepth?.legalEupMyeonDongName,
            siGunGuName: regionByDepth?.siGunGuName,
            siDoName: regionByDepth?.siDoName ?? '',
          },
        };
      }, [regionByDepth, searchHospitalsParams]);

    const searchHospitalsQuery = useSearchInfluenzaHospitalsInfinityQuery({
      ...getSearchHospitalsParams(),
    });

    const { data: addresses, refetch: refetchAddresses } =
      useGetUserAddresses();

    const updateCoordinate = (coord: UserCoordinate) => {
      initRegionFilter();
      return setCoordinate((prev) =>
        SearchHospitalService.updateCoordinate(prev, coord),
      );
    };

    const updatePureCoordinate = (coord: UserCoordinate) => {
      return setCoordinate((prev) =>
        SearchHospitalService.updateCoordinate(prev, coord),
      );
    };

    const getHeaderAddress = () => {
      let address = `${sigunguDongli.sigungu} ${sigunguDongli.dongLi}`;

      if (regionByDepth?.siDoName) {
        if (
          !regionByDepth.siGunGuName &&
          !regionByDepth.legalEupMyeonDongName
        ) {
          address = `${regionByDepth.siDoName}`;
        } else if (
          regionByDepth.siGunGuName &&
          !regionByDepth.legalEupMyeonDongName
        ) {
          address = `${regionByDepth.siDoName} ${regionByDepth.siGunGuName}`;
        } else if (
          regionByDepth.siGunGuName &&
          regionByDepth.legalEupMyeonDongName
        ) {
          address = `${regionByDepth.siGunGuName} ${regionByDepth.legalEupMyeonDongName}`;
        } else if (
          !regionByDepth.siGunGuName &&
          regionByDepth.legalEupMyeonDongName
        ) {
          address = `${regionByDepth.siDoName} ${regionByDepth.legalEupMyeonDongName}`;
        }
      }

      return address;
    };

    const switchRegionFilter = () => {
      // 지역필터 모달 오픈시
      if (!isRegionFilterOpened) {
        faceTofaceLogger({
          pageName,
          eventName: 'click_region_filter',
        });
      }

      setIsRegionFilterOpened(!isRegionFilterOpened);
    };

    const selectRegion = (region: RegionName) => {
      setRegionByDepth({
        siDoName: region.siDoName,
        siGunGuName: region?.siGunGuName,
        legalEupMyeonDongName: region?.dongName,
      });
      setIsRegionFilterOpened(false);
    };

    const changeSiDo = (siDo: SearchKoreaAddressV2Vo, isAll?: boolean) => {
      setSelectedRegion({
        siDo,
        siGunGu: null,
        dong: null,
      });

      if (isAll) {
        selectRegion({
          siDoName: siDo.regionName,
          siGunGuName: undefined,
          dongName: undefined,
        });
      }
    };

    const clickSiDo =
      (item: SearchKoreaAddressV2Vo, isAll?: boolean) =>
      (event: MouseEvent<HTMLDivElement>) => {
        faceTofaceLogger({
          pageName,
          eventName: 'click_region_filter_find',
          payload: {
            sector_1: item.regionName,
          },
        });
        event.stopPropagation();
        changeSiDo(item, isAll);
      };

    const changeSiGunGu = (
      siGunGu: SearchKoreaAddressV2Vo,
      isAll?: boolean,
    ) => {
      if (!selectedRegion?.siDo) {
        return;
      }

      if (!isAll && siGunGu.regionCode === selectedRegion.siGunGu?.regionCode) {
        setSelectedRegion({
          ...selectedRegion,
          siGunGu: null,
          dong: null,
        });
        return;
      }

      setSelectedRegion({
        ...selectedRegion,
        siGunGu,
        dong: null,
      });

      if (isAll) {
        selectRegion({
          siDoName: selectedRegion.siDo.regionName,
          siGunGuName: siGunGu.regionName,
        });
      }
    };

    const clickSiGunGu =
      (item: SearchKoreaAddressV2Vo, isAll?: boolean) =>
      (event: MouseEvent<HTMLDivElement>) => {
        faceTofaceLogger({
          pageName,
          eventName: 'click_region_filter_find',
          payload: {
            sector_1: selectedRegion.siDo?.regionName ?? '',
            sector_2: item.regionName,
          },
        });
        event.stopPropagation();
        changeSiGunGu(item, isAll);
      };

    const changeDong = (dong: SearchKoreaAddressV2Vo) => {
      if (!dong.regionCode || !selectedRegion.siDo) return;

      setSelectedRegion({
        ...selectedRegion,
        dong,
      });

      selectRegion({
        siDoName: selectedRegion.siDo.regionName,
        siGunGuName: selectedRegion.siGunGu?.regionName,
        dongName: dong?.regionName,
      });
    };

    const clickDong =
      (item: SearchKoreaAddressV2Vo) => (event: MouseEvent<HTMLDivElement>) => {
        faceTofaceLogger({
          pageName,
          eventName: 'click_region_filter_find',
          payload: {
            sector_1: selectedRegion.siDo?.regionName ?? '',
            sector_2: selectedRegion.siGunGu?.regionName ?? '',
            sector_3: item.regionName,
          },
        });
        event.stopPropagation();
        changeDong(item);
      };

    const initRegionFilter = () => {
      setSelectedRegion({
        siDo: null,
        siGunGu: null,
        dong: null,
      });
      setRegionByDepth({});
    };

    const requestGeolocationPermission = useCallback(() => {
      initRegionFilter();
      if (!navigator.geolocation) {
        showToast({
          message: '다른 브라우저로 해당 기능을 이용해주세요',
        });
        updateCoordinate(initialCoord);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          updateCoordinate({
            latitude: coords.latitude,
            longitude: coords.longitude,
          });
        },
        () => {
          showToast({
            message: '위치 사용을 허용을 해야 내 위치 설정이 가능해요',
          });
        },
        { enableHighAccuracy: true },
      );
    }, [initialCoord, showToast]);

    const getCurrentLocation = () => {
      faceTofaceLogger({
        pageName,
        eventName: 'click_region_filter_my_location',
      });
      requestGeolocationPermission();
      setIsRegionFilterOpened(false);
    };

    const goToAppHospital = (hospitalPid: string) => {
      if (!isWebView()) {
        return;
      }

      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: ROUTES.HOSPITAL.DETAIL.appScreenName,
        params: { hospitalPid: hospitalPid, type: 'influenza' },
      });
    };

    const goToAppMyPageAddressScreen = () => {
      if (!isWebView()) {
        return;
      }
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: 'AddressStack',
        params: { screen: ROUTES.ADDRESS.HOME.appScreenName },
      });
    };

    const selectSavedAddress = (coordiname: UserCoordinate) => () => {
      updateCoordinate(coordiname);
      switchRegionFilter();
    };

    /**
     * queryDepartmentId 유무에 따라 초기 departmentId를 다르게 가져가기 위함
     */

    useEffect(() => {
      if (!userCoordinateByIp) {
        return;
      }

      updatePureCoordinate(userCoordinateByIp);
    }, [userCoordinateByIp]);

    useEffect(() => {
      if (isFocused && isLoggedIn) {
        refetchAddresses();
      }
    }, [isFocused, isLoggedIn]);

    useEffect(() => {
      addressFetch();
    }, [coordinate, addressFetch]);

    useEffect(() => {
      if (isRegionFilterOpened) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }, [isRegionFilterOpened]);

    useEffect(() => {
      if (!isEmpty(siDoList) && !selectedRegion.siDo) {
        const seoul = find(siDoList, { regionCode: '11' });

        if (seoul) {
          changeSiDo(seoul);
        }
      }
    }, [siDoList, selectedRegion.siDo]);

    useEffect(() => {
      window.scrollTo({ top: 0 });
    }, [getSearchHospitalsParams()]);

    return {
      goToAppHospital,
      goToAppMyPageAddressScreen,
      selectSavedAddress,
      hospitals: searchHospitalsQuery.data ?? [],
      isLoadingHospitals: searchHospitalsQuery.isLoading,
      hasNoData: !searchHospitalsQuery.data?.length,
      updateCoordinate,
      fetchNextPage: searchHospitalsQuery.fetchNextPage,
      hasNextPage: searchHospitalsQuery.hasNextPage ?? false,
      requestGeolocationPermission,
      addresses,
      switchRegionFilter,
      isRegionFilterOpened,
      siDoList,
      siGunGuList,
      dongList,
      changeSiDo,
      regionByDepth,
      getHeaderAddress,
      selectedRegion,
      getCurrentLocation,
      clickSiDo,
      clickSiGunGu,
      clickDong,
      isSejong,
      ...influenzaHospitalFilterHook,
    };
  };

export default useInfluenzaHospitalListViewModel;
